/**
 * Tab dell'app "Servizi ICT ENEA".
 *
 * La pagina serve due tab statiche: quella di Assistenza punta direttamente al
 * sito (è incorniciabile da Teams), quella di PAM mostra questo elenco perché
 * PAM360 invia `X-Frame-Options: SAMEORIGIN` e non può stare in un iframe.
 * Il parametro `?tab=<id>` nel contentUrl decide cosa mostrare.
 */
import { initTeams } from './teams.ts';
import { openLabel, pageSubtitle, resolveSites, type IctSite } from './lib/sites.ts';
import './styles.css';

const el = {
  title: document.getElementById('app-title') as HTMLElement,
  subtitle: document.getElementById('app-subtitle') as HTMLElement,
  sites: document.getElementById('sites') as HTMLElement,
  status: document.getElementById('status') as HTMLElement,
  context: document.getElementById('context') as HTMLElement,
};

const tabParam = new URLSearchParams(window.location.search).get('tab');
const visible = resolveSites(tabParam);

function applyTheme(theme: string): void {
  document.documentElement.dataset.theme = theme;
}

/** Crea la scheda di un servizio con il pulsante di apertura. */
function siteCard(site: IctSite): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';

  const title = document.createElement('h2');
  title.className = 'card__title';
  title.textContent = site.name;

  const desc = document.createElement('p');
  desc.className = 'card__desc';
  desc.textContent = site.description;

  const url = document.createElement('p');
  url.className = 'card__url';
  url.textContent = site.url;

  const button = document.createElement('a');
  button.className = 'btn btn--primary';
  button.href = site.url;
  button.target = '_blank';
  button.rel = 'noopener';
  button.textContent = openLabel(site);
  button.addEventListener('click', (ev) => {
    // In Teams il deep link apre il browser di sistema; fuori dal frame il
    // link normale basta.
    if (client?.context.inTeams) {
      ev.preventDefault();
      void openSite(site);
    }
  });

  card.append(title, desc, url);
  if (site.note) {
    const note = document.createElement('p');
    note.className = 'card__note';
    note.textContent = site.note;
    card.append(note);
  }
  card.append(button);
  return card;
}

let client: Awaited<ReturnType<typeof initTeams>> | null = null;

async function openSite(site: IctSite): Promise<void> {
  try {
    await client?.openLink(site.url);
    el.status.textContent = `${site.name}: aperto nel browser.`;
  } catch {
    window.open(site.url, '_blank', 'noopener');
    el.status.textContent = `${site.name}: aperto in una nuova scheda.`;
  }
}

async function main(): Promise<void> {
  client = await initTeams();
  applyTheme(client.context.theme);
  client.onThemeChange(applyTheme);

  el.title.textContent = visible.length === 1 ? (visible[0]?.name ?? 'Servizi ICT ENEA') : 'Servizi ICT ENEA';
  el.subtitle.textContent = pageSubtitle(visible);
  el.sites.replaceChildren(...visible.map(siteCard));

  if (!client.context.inTeams) {
    el.status.textContent = 'Modalità browser: i servizi si aprono in una nuova scheda.';
  }

  const who = client.context.userDisplayName;
  el.context.textContent = who ? `Accesso come ${who}` : '';
}

void main();
